import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { cloneDeep, isEqual, isNil } from 'lodash-es';
import {
  getV7DashboardsGlobalFilterGetByDashboardId,
  postV7DashboardsGlobalFilterByDashboardId,
} from '@/api-client/dashboard/queries/integrated-dashboard-filter-v7-controller/integrated-dashboard-filter-v7-controller';
import type { GlobalFilterItem } from '@/api-client/dashboard/model';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getV7NdmDeviceMonitoringInterfaceByDeviceId } from '@/api-client/ndm/queries/ndm-monitoring-controller/ndm-monitoring-controller';
import { getV9AlertsTriggeredTargetsByAlertResultId } from '@/api-client/alert/queries/alert-common-v9-controller/alert-common-v9-controller';
import { convertGlobalFilterValueToTargetTagData } from '@/dashboard/features/globalVariables/services/adaptor';
import type {
  TargetTagData,
  GlobalVariable,
  SelectedGlobalVariable,
  UpdatedGlobalVariablesRequest,
} from '@/dashboard/features/globalVariables/domain/globalVariable.types';
import {
  resolveVariableType,
  convertToGlobalVariable,
} from '@/dashboard/features/globalVariables/domain/standardVariables.utils';
import { useDashboardViewStore } from '../dashboard-view';

const TAG_SEPARATOR = '+';

export const useStandardVariablesStore = defineStore('standardVariables', () => {
  const { monitoringDashboard } = storeToRefs(useDashboardViewStore());
  const monitoringDashboardId = computed(() => monitoringDashboard.value.dashboardId);

  const standardVariables = ref<GlobalVariable[]>([]);
  const migratedFilterIdMap = ref<Map<number, number>>(new Map());
  const selectedStandardVariables = ref<SelectedGlobalVariable[]>([]);
  const updatedStandardVariableId = ref<number>();
  const isFetching = ref(false);
  // 편집 저장마다 증가하는 세션 카운터. 에디터 모드 위젯 요청 본문 솔트로 쓰여,
  // 본문이 동일한 재편집에서도 서버 캐시 키가 분리되도록 한다.
  const editRevision = ref(0);
  const bumpEditRevision = () => {
    editRevision.value += 1;
  };

  const fetchAllTargetsByFilterId = async (globalFilterId: number): Promise<TargetTagData[]> => {
    const dashboardId = monitoringDashboardId.value;
    try {
      const { data } = await getV7DashboardsGlobalFilterGetByDashboardId(
        dashboardId,
        { globalFilterId },
        { params: { frameName: FRAME_NAMES.DASHBOARD.GLOBAL_VARIABLES } },
      );

      return (
        data?.data?.at(0)?.globalFilterKeyValues?.map(convertGlobalFilterValueToTargetTagData) ?? []
      );
    } catch {
      //
      return [];
    }
  };

  const findStandardVariableById = (globalFilterId: number) =>
    standardVariables.value.find((item) => item.globalFilterId === globalFilterId);

  const fetchStandardVariables = async () => {
    const dashboardId = monitoringDashboardId.value;
    isFetching.value = true;
    try {
      const { data } = await getV7DashboardsGlobalFilterGetByDashboardId(
        dashboardId,
        {},
        { frameName: FRAME_NAMES.DASHBOARD.GLOBAL_VARIABLES },
      );

      standardVariables.value = await Promise.all(
        data.data?.map(async (item: GlobalFilterItem) => {
          const variableType = resolveVariableType(item);
          const targets =
            item.globalFilterKeyValues?.map(convertGlobalFilterValueToTargetTagData) ?? [];
          const allTargets =
            variableType === 'target'
              ? await fetchAllTargetsByFilterId(item.globalFilterId ?? -1)
              : targets;

          return convertToGlobalVariable(item, allTargets);
        }) ?? [],
      );

      if (standardVariables.value.length) {
        migratedFilterIdMap.value = new Map(
          standardVariables.value
            .filter((i) => !isNil(i.oldGlobalFilterId))
            .map((item) => [item.oldGlobalFilterId!, item.globalFilterId!]),
        );
      }
    } catch {
      //
    } finally {
      isFetching.value = false;
    }
  };

  const fetchStandardVariableById = async (globalFilterId: number) => {
    try {
      const allTargets = await fetchAllTargetsByFilterId(globalFilterId);
      const standardVariable = findStandardVariableById(globalFilterId);
      if (standardVariable && allTargets.length) {
        standardVariable.allTargets = allTargets;
      }
    } catch {
      //
    }
  };

  const updateStandardVariables = async ({
    updateFilters,
    addFilters,
    deleteGlobalFilterIds,
  }: UpdatedGlobalVariablesRequest) => {
    const dashboardId = monitoringDashboardId.value;
    await postV7DashboardsGlobalFilterByDashboardId(
      dashboardId,
      {
        updateFilters,
        addFilters,
      },
      {
        deleteGlobalFilterIds,
      },
      {
        params: { frameName: FRAME_NAMES.DASHBOARD.GLOBAL_VARIABLES_UPDATE },
      },
    );
  };

  const updateStandardVariablesSelectedById = (
    globalFilterId: number,
    selectedList: TargetTagData[],
  ) => {
    const standardVarSelected = selectedStandardVariables.value.find(
      (item) => item.globalFilterId === globalFilterId,
    );

    if (!standardVarSelected) return;
    if (isEqual(standardVarSelected.selectedList, selectedList)) return;

    updatedStandardVariableId.value = globalFilterId;
    standardVarSelected.selectedList = cloneDeep(selectedList);
  };

  const updateSelectedStandardVariablesByAllTarget = (targetIds: string[]) => {
    const targetIdSet = new Set(targetIds);

    standardVariables.value.forEach((filterItem) => {
      const filteredTargets = filterItem.allTargets.filter((i) => targetIdSet.has(i.tagId));
      updateStandardVariablesSelectedById(filterItem.globalFilterId, filteredTargets);
    });
  };

  const replaceSelectedStandardVariables = (selectedList: SelectedGlobalVariable[]) => {
    if (isEqual(selectedStandardVariables.value, selectedList)) return;
    // 전체 교체 경로(preset 적용 등)는 "특정 변수 변경"이 아니므로 추적 ID를 초기화한다.
    // 유지하면 reference 위젯의 shouldCallApiOnGlobalVariableChange가 무관한 변경에 매칭될 수 있다.
    updatedStandardVariableId.value = undefined;
    selectedStandardVariables.value = selectedList;
  };

  const replaceStandardVariables = (variables: GlobalVariable[]) => {
    if (isEqual(standardVariables.value, variables)) return;
    standardVariables.value = variables;
  };

  const handleDatabaseQueryTag = (tags: string[]) => {
    const targetIds = tags.map((tag) => tag.split(TAG_SEPARATOR)[1]);
    updateSelectedStandardVariablesByAllTarget(targetIds);
  };

  const handleNetworkDeviceQueryTag = async (
    targetId: string,
    matchedFilter: GlobalVariable,
    selectedVariables: TargetTagData[],
  ) => {
    const { data } = await getV7NdmDeviceMonitoringInterfaceByDeviceId(targetId);
    const interfaceList = data.data ?? [];

    if (interfaceList.length > 0) {
      standardVariables.value.forEach((filterItem) => {
        if (filterItem.globalFilterId === matchedFilter.globalFilterId) {
          updateStandardVariablesSelectedById(matchedFilter.globalFilterId, selectedVariables);
        } else {
          const filteredTargets = filterItem.targets.filter((i) =>
            interfaceList.some((tObj) => `${targetId}|${tObj.index}` === i.tagId),
          );
          updateStandardVariablesSelectedById(filterItem.globalFilterId, filteredTargets);
        }
      });
    }
  };

  const findTargetByNamespace = (
    matchedFilter: GlobalVariable,
    namespace: string,
  ): TargetTagData | undefined => {
    return matchedFilter.allTargets.find(({ tagName }) => tagName === namespace);
  };

  const findTargetByTagId = (
    matchedFilter: GlobalVariable,
    targetId: string,
  ): TargetTagData | undefined => {
    return matchedFilter.allTargets.find(({ tagId }) => tagId === targetId);
  };

  const updateSelectedStandardVariablesByQueryTag = async (tags: string[]) => {
    const firstTag = tags[0];
    if (!firstTag || !standardVariables.value?.length) return;

    const [target, targetId, namespace] = firstTag.split(TAG_SEPARATOR);
    const matchedFilter = standardVariables.value.find((v) => v.tagKey === target);

    if (target === 'database') {
      handleDatabaseQueryTag(tags);
      return;
    }

    if (!matchedFilter) return;

    // namespace 케이스 (e.g., kubernetes namespace)
    if (namespace && namespace !== 'undefined') {
      const targetData = findTargetByNamespace(matchedFilter, namespace);
      if (targetData) {
        updateStandardVariablesSelectedById(matchedFilter.globalFilterId, [targetData]);
      }
      return;
    }

    // 일반 케이스
    const targetData = findTargetByTagId(matchedFilter, targetId);
    if (!targetData) return;

    if (target === 'network_device') {
      await handleNetworkDeviceQueryTag(targetId, matchedFilter, [targetData]);
    } else {
      updateStandardVariablesSelectedById(matchedFilter.globalFilterId, [targetData]);
    }
  };

  const updateSelectedStandardVariablesByQueryAlert = async (
    alertId: string,
    fromTime: string,
    toTime: string,
  ) => {
    if (!alertId) return;

    const { data } = await getV9AlertsTriggeredTargetsByAlertResultId(alertId, {
      fromTime,
      toTime,
    });

    const targetList = data.data ?? [];
    if (targetList.length > 0) {
      standardVariables.value.forEach((filterItem) => {
        const filteredTargets = filterItem.allTargets.filter((i) =>
          targetList.some((tObj) => tObj.targetId === i.tagId),
        );
        updateStandardVariablesSelectedById(filterItem.globalFilterId, filteredTargets);
      });
    }
  };

  const resetState = () => {
    standardVariables.value = [];
    migratedFilterIdMap.value = new Map();
    selectedStandardVariables.value = [];
    updatedStandardVariableId.value = undefined;
    editRevision.value = 0;
  };

  return {
    standardVariables,
    migratedFilterIdMap,
    selectedStandardVariables,
    updatedStandardVariableId,
    isFetching,
    editRevision,

    fetchStandardVariables,
    fetchStandardVariableById,
    updateStandardVariables,
    updateStandardVariablesSelectedById,
    updateSelectedStandardVariablesByAllTarget,
    updateSelectedStandardVariablesByQueryTag,
    updateSelectedStandardVariablesByQueryAlert,
    replaceStandardVariables,
    replaceSelectedStandardVariables,
    bumpEditRevision,
    resetState,
  };
});
